import { makeStyles } from '@material-ui/styles'
import clsx from 'clsx'
import React, { useState } from 'react'
import CopyToClipboard from 'core/components/CopyToClipboard'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import Text from 'core/elements/Text'
import Theme from 'core/themes/model'

export interface TooltipProps<T> {
  items: T[]
  nameKey?: keyof T
  className?: string
  renderIcon?: (item: T) => React.ReactNode
  showCopy?: boolean
}

export default function TooltipListBody<T>({
  items,
  className,
  nameKey,
  renderIcon,
  showCopy = true,
}: TooltipProps<T>) {
  const classes = useStyles({})
  return (
    <ul className={clsx(classes.tooltipListBody, className)}>
      {items.map((item, idx) => (
        <TooltipListItem
          key={idx}
          item={item}
          nameKey={nameKey}
          isFirst={idx === 0}
          isLast={idx === items.length - 1}
          Icon={renderIcon}
          showCopy={showCopy}
        />
      ))}
    </ul>
  )
}

function TooltipListItem(props) {
  const { item, isFirst, isLast, Icon, nameKey, showCopy } = props
  const [copied, setCopied] = useState(false)
  const clickedOverlay = () => {
    setCopied(true)
    setTimeout(() => {
      setCopied(false)
    }, 1000)
  }
  const classes = useStyles()
  const text = nameKey ? item[nameKey] : item
  // eslint-disable-next-line no-extra-boolean-cast
  const iconContent = !!Icon ? (
    <div
      className={clsx(classes.copyContainer, {
        [classes.copyContainerFirst]: isFirst && !isLast,
        [classes.copyContainerLast]: isLast && !isFirst,
        [classes.copyContainerFirstAndLast]: isFirst && isLast,
      })}
    >
      <Icon {...item} className={clsx(classes.tooltipText, classes.tooltipCopyIcon)} />
    </div>
  ) : showCopy ? (
    <CopyToClipboard copyText={text} copyIcon={false} inline={false} triggerWithChild>
      <div
        className={clsx(classes.copyContainer, {
          [classes.copyContainerFirst]: isFirst && !isLast,
          [classes.copyContainerLast]: isLast && !isFirst,
          [classes.copyContainerFirstAndLast]: isFirst && isLast,
        })}
        onClick={clickedOverlay}
      >
        <FontAwesomeIcon size="md" className={clsx(classes.tooltipText, classes.tooltipCopyIcon)}>
          {copied ? 'check' : 'copy'}
        </FontAwesomeIcon>
      </div>
    </CopyToClipboard>
  ) : null
  return (
    <li className={classes.tooltipListItem}>
      <Text variant="body2" className={classes.tooltipText} noWrap>
        {text}
      </Text>
      {iconContent}
    </li>
  )
}
interface StyleProps {
  isFirst?: boolean
  isLast?: boolean
}
const useStyles = makeStyles<Theme>((theme) => ({
  tooltipListBody: {
    margin: 0,
    padding: 0,
  },
  tooltipListItem: {
    display: 'grid',
    gridTemplateColumns: '1fr max-content',
    gridAutoFlow: 'column',
    alignItems: 'center',
    gap: 8,
    paddingLeft: 8,
    height: 36,
    borderTop: `1px solid ${theme.components.tooltip.border}`,

    '&:first-child': {
      borderTop: 'none',
    },
  },
  tooltipCopyIcon: {
    cursor: 'inherit',
    fontSize: 16,
  },
  tooltipText: {
    color: theme.components.tooltip.text,
    maxWidth: 350,
  },
  copyContainer: {
    cursor: 'pointer',
    backgroundColor: theme.components.tooltip.copyBackground,
    width: 36,
    height: 36,
    boxSizing: 'border-box',
    display: 'grid',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '0px 0px 0px 0px',
  },
  copyContainerFirst: {
    borderRadius: '0px 4px 0px 0px',
  },
  copyContainerLast: {
    borderRadius: '0px 0px 4px 0px',
  },
  copyContainerFirstAndLast: {
    borderRadius: '0px 4px 4px 0px',
  },
}))
